import dayjs from 'dayjs';
import type { PpeRequiredItem, SizCategory, SizUnit } from '@/entities/siz';

export const SIZ_CATEGORY_LABELS: Record<SizCategory, string> = {
  clothing: 'Одежда',
  footwear: 'Обувь',
  head_protection: 'Защита головы',
  eye_protection: 'Защита глаз',
  hand_protection: 'Защита рук',
  respiratory_protection: 'Защита дыхания',
  fall_protection: 'Защита от падения',
  hearing_protection: 'Защита слуха',
  other: 'Прочее',
};

export const SIZ_UNIT_LABELS: Record<SizUnit, string> = {
  piece: 'шт.',
  pair: 'пар',
  kit: 'компл.',
  set: 'набор',
};

const SIZE_FIELD_ORDER = ['height', 'clothing', 'footwear', 'headwear', 'respirator', 'gloves'] as const;

const SIZE_FIELD_LABELS: Record<string, string> = {
  height: 'Рост',
  clothing: 'Одежда',
  footwear: 'Обувь',
  headwear: 'Гол. убор',
  respirator: 'СИЗОД',
  gloves: 'Перчатки',
};

const SIZE_UNIT_BY_FIELD: Record<string, string> = {
  height: 'см',
};

export interface SizSizeField {
  key: string;
  label: string;
  value: string;
}

export function buildSizeFields(sizes: Record<string, string> | undefined): SizSizeField[] {
  if (!sizes) return [];
  const seen = new Set<string>();
  const ordered: SizSizeField[] = [];
  for (const key of SIZE_FIELD_ORDER) {
    const value = sizes[key];
    if (value) {
      seen.add(key);
      ordered.push({ key, label: SIZE_FIELD_LABELS[key], value: withUnit(key, value) });
    }
  }
  for (const [key, value] of Object.entries(sizes)) {
    if (seen.has(key) || !value) continue;
    ordered.push({ key, label: SIZE_FIELD_LABELS[key] ?? humanize(key), value: withUnit(key, value) });
  }
  return ordered;
}

function withUnit(key: string, value: string): string {
  const unit = SIZE_UNIT_BY_FIELD[key];
  return unit ? `${value} ${unit}` : value;
}

function humanize(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
}

export interface SizSummary {
  total: number;
  inDate: number;
  expiring: number;
  missing: number;
  inDateRatio: string;
}

export function buildSummary(items: PpeRequiredItem[]): SizSummary {
  let inDate = 0;
  let expiring = 0;
  let missing = 0;
  for (const item of items) {
    if (item.status === 'issued') inDate += 1;
    else if (item.status === 'expiring_soon') expiring += 1;
    else missing += 1;
  }
  return {
    total: items.length,
    inDate,
    expiring,
    missing,
    inDateRatio: `${inDate} из ${items.length} в сроке`,
  };
}

export interface SizRowState {
  item: PpeRequiredItem;
  title: string;
  subtitle: string;
}

export function buildRows(items: PpeRequiredItem[]): SizRowState[] {
  return items.map((item) => ({
    item,
    title: item.name,
    subtitle: composeNormSubtitle(item),
  }));
}

function composeNormSubtitle(item: PpeRequiredItem): string {
  const qty = stripTrailingZeros(item.requiredQuantity);
  const unit = SIZ_UNIT_LABELS[item.unit] ?? item.unit;
  const parts = [`По норме: ${qty} ${unit}`];
  if (item.nextReplacementAt) parts.push(`следующая замена с ${formatRuDate(item.nextReplacementAt)}`);
  return parts.join(' · ');
}

export interface SizAlert {
  missing: string[];
  expiring: { name: string }[];
}

export function buildAlert(rows: SizRowState[]): SizAlert | null {
  const missing: string[] = [];
  const expiring: SizAlert['expiring'] = [];
  for (const row of rows) {
    if (row.item.status === 'pending' || row.item.status === 'overdue') missing.push(row.item.name);
    else if (row.item.status === 'expiring_soon') expiring.push({ name: row.item.name });
    else if (row.item.status === 'partially_issued') missing.push(row.item.name);
  }
  if (missing.length === 0 && expiring.length === 0) return null;
  return { missing, expiring };
}

export function formatRuDate(iso: string): string {
  return dayjs(iso).format('DD.MM.YYYY');
}

function stripTrailingZeros(value: string): string {
  if (!value.includes('.')) return value;
  return value.replace(/\.?0+$/, '');
}
